<template>
  <div class="inner" :style="containerStyles" :class="{ loading: store.getters['modal/getLoadingState']() }">
    <div class="auto">
      <div v-show="store.getters['modal/getLoadingState']()" class="loader">
        <LoadingSpinner />
      </div>
      <div v-if="props.heroImage" class="hero-image">
        <img :src="props.heroImage" alt="Hero Image">
      </div>
      <div class="modal-container" :class="{ hideComponent: store.getters['modal/getLoadingState']() }">
        <div class="custom-header-wrapper">
          <slot name="custom-header">
            <ModalHeader v-if="!noHeader" v-bind="props" @close-action="closeIconAction" />
          </slot>
        </div>
        <div v-if="!props.noCustomComponent" class="custom-component-wrapper" :style="componentWrapperStyle">
          <slot name="custom-component" />
        </div>
        <div class="custom-footer-wrapper">
          <slot name="custom-footer">
            <ModalFooter v-if="!noFooter" v-bind="props" @close-action="closeAction" />
          </slot>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStore } from 'vuex'
import LoadingSpinner from '@/components/common/LoadingSpinner.vue'
import ModalHeader from './ModalHeader.vue'
import ModalFooter from './ModalFooter.vue'
import { CustomModalProps } from '@/interfaces'

const store = useStore()
const props = defineProps<CustomModalProps>()
const emit = defineEmits(['close-action', 'close-icon-action'])

const styleProps = ['height', 'width', 'padding', 'margin', 'maxWidth', 'maxHeight'] as const

const containerStyles = computed(() => {
  const styles: Record<string, string> = {}

  styleProps.forEach((prop) => {
    if (props[prop]) {
      styles[prop] = props[prop] as string
    }
  })

  return styles
})

const componentWrapperStyle = computed(() => {
  if (props.componentPadding) {
    return { padding: props.componentPadding }
  }
  return {}
})

const closeAction = async () => {
  emit('close-action')
}

const closeIconAction = () => {
  emit('close-icon-action')
}
</script>

<style scoped lang="scss">
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

.inner {
  background: #ffffff;
  border-radius: 1.5rem;
  position: relative;
  color: black !important;
  justify-content: center;
  align-items: center;
  max-height: 100vh;
  max-width: 90vw;

  .hero-image {
    width: 100%;

    img {
      width: 100%;
      height: auto;
      object-fit: contain;
      display: block;
    }
  }

  &.loading {
    .auto {
      display: flex;
      justify-content: center;
      align-items: center;

      .loader {
        position: absolute;
        display: flex;
        justify-content: center;
        align-items: center;
      }
    }
  }

  .hideComponent {
    opacity: 0;
    pointer-events: none;
  }

  .modal-container {
    position: relative;
    max-height: 95vh;
    display: flex;
    flex-direction: column;
    padding: 2rem;

    @media (max-width: $sm) {
      padding: 1rem 1.5rem;
    }

    .custom-header-wrapper {
      flex-shrink: 0;
    }
    .custom-footer-wrapper {
      flex-shrink: 0;
      padding: 0.75rem;
    }
    .custom-component-wrapper {
      flex-grow: 1;
      min-height: 0;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 12px;
      display: flex;
      scrollbar-width: thin;
      scrollbar-color: #dadbe8 white;

      &::-webkit-scrollbar {
        width: 4px;
        height: 4px;
      }

      &::-webkit-scrollbar-track {
        background: white;
      }

      &::-webkit-scrollbar-thumb {
        background: #dadbe8;
        border-radius: 10px;
      }

      &::-webkit-scrollbar-thumb:hover {
        background: #555;
      }
    }
  }
}
</style>
